"use client";

import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownTextProps {
  children: string;
  mode?: "block" | "inline";
  className?: string;
  components?: Components;
}

export const blockMarkdownComponents: Components = {
  p: ({ children }) => <p className="my-4 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-4 list-disc pl-10 first:mt-0 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 list-decimal pl-10 first:mt-0 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="my-1">{children}</li>,
  h1: ({ children }) => (
    <h1 className="my-6 text-2xl font-bold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="my-5 text-xl font-bold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="my-4 text-lg font-bold first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="my-4 text-base font-semibold first:mt-0">{children}</h4>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-4 border-gray-300 pl-4 text-gray-600 italic first:mt-0 last:mb-0">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse border border-gray-200">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left text-xs font-medium">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-2 py-1 text-xs">{children}</td>
  ),
};

function hasBlockMarkdownSyntax(text: string): boolean {
  return (
    /^\s*[-*+]\s/m.test(text) ||
    /^\s*\d+\.\s/m.test(text) ||
    /^\s*#{1,6}\s/m.test(text) ||
    /^\s*>\s/m.test(text) ||
    /^\s*\|.+\|\s*$/m.test(text) ||
    /\n\s*\n/.test(text)
  );
}

export function MarkdownText({
  children,
  mode = "block",
  className,
  components,
}: MarkdownTextProps) {
  if (!children) return <span>—</span>;

  if (mode === "inline") {
    const singleLine = children.replace(/\n+/g, " ").trim();
    return (
      <span className={className}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          allowedElements={["strong", "em", "code", "del", "a"]}
          unwrapDisallowed
        >
          {singleLine}
        </ReactMarkdown>
      </span>
    );
  }

  const processed = hasBlockMarkdownSyntax(children)
    ? children
    : children.replace(/\n/g, "  \n");
  return (
    <div className={`max-w-none ${className ?? ""}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ ...blockMarkdownComponents, ...components }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}
